/**
 * Express Checkout Module
 * Handles Stripe Express Checkout (Apple Pay, Google Pay, Link) across multiple contexts
 */

import { showError, updateTotals, getCsrfToken, parseCartItems, calculateTotalAmount } from './shared.js';
import { showProcessingModal, hideProcessingModal } from '../core/loading.js';

function buildGa4Items(cartItems) {
    return cartItems.map(item => ({
        'item_name': item.name || '',
        'item_id': item.sku || '',
        'price': (item.unitAmount || 0) / 100,
        'quantity': item.quantity || 1,
        'item_brand': 'MARSHALLTOWN'
    }));
}

const EXPRESS_MODAL_OPTIONS = {
    processingTitle: 'Processing Your Order',
    processingText: 'Please wait while we securely process your payment...',
    successTitle: 'Order Placed Successfully!',
    successText: 'Redirecting to your order confirmation...'
};

/**
 * Parse the express checkout config JSON from the section element
 * @param {HTMLElement} section - Express checkout section element
 * @returns {Object|null} - Parsed config or null
 */
export function parseExpressCheckoutConfig(section) {
    const configEl = section.querySelector('[data-mcom="express-checkout-config"]');

    if (!configEl) {
        console.error('Express Checkout: Config element not found');
        return null;
    }

    try {
        return JSON.parse(configEl.textContent);
    } catch (e) {
        console.error('Express Checkout: Failed to parse config:', e);
        return null;
    }
}

/**
 * Initialize Express Checkout
 * @param {Object} config - Configuration object
 * @param {HTMLElement} config.section - Express checkout section element
 * @param {Object} config.stripe - Stripe instance
 * @param {string} config.context - Context (ProductPage, CartPage, CheckoutPage)
 * @param {Object} config.urls - URL endpoints from server config
 * @param {string} config.cartItemsJson - JSON string of cart items
 * @param {Function} config.onAddToCart - Optional async callback to add product to cart (ProductPage only)
 * @param {Function} config.onCleanup - Optional async callback to clear cart on cancel/failure (ProductPage only)
 * @returns {Object} - Express checkout instance
 */
export function initializeExpressCheckout(config) {
    const { section, stripe, context, urls, cartItemsJson, onAddToCart, onCleanup } = config;

    if (!section) {
        console.error('Express Checkout: Section element required');
        return null;
    }

    if (!stripe) {
        console.error('Express Checkout: Stripe instance required');
        return null;
    }

    console.log(`Express Checkout: Initializing for ${context} context`);

    const cartItems = parseCartItems(cartItemsJson);

    if (!cartItems.length) {
        console.log('Express Checkout: No cart items found');
        return null;
    }

    const amountInCents = calculateTotalAmount(cartItems);

    // Create Stripe Elements
    const elements = stripe.elements({
        mode: 'payment',
        amount: amountInCents,
        currency: 'usd',
        setup_future_usage: 'off_session',
        capture_method: 'manual'
    });

    // Create Express Checkout Element
    const expressCheckoutElement = elements.create('expressCheckout', {
        emailRequired: true,
        shippingAddressRequired: true,
        paymentMethods: {
            applePay: 'auto',
            googlePay: 'auto',
            link: 'auto'
        },
        buttonType: {
            applePay: 'buy',
            googlePay: 'buy'
        },
        layout: {
            maxColumns: 3,
            maxRows: 3,
            overflow: 'auto'
        }
    });

    // State
    let selectedShippingMethod = null;
    let cartModified = false;
    let shippingInfoPushed = false;

    // Event handlers
    expressCheckoutElement.on('shippingaddresschange', async (event) => {
        await handleShippingAddressChange(event, urls, elements, onAddToCart, (modified) => { cartModified = modified; });
    });

    expressCheckoutElement.on('shippingratechange', async (event) => {
        selectedShippingMethod = event.shippingRate;

        if (!shippingInfoPushed) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'add_shipping_info',
                'ecommerce': {
                    'currency': 'USD',
                    'value': amountInCents / 100,
                    'shipping_tier': event.shippingRate.displayName,
                    'items': buildGa4Items(cartItems)
                }
            });
            shippingInfoPushed = true;
        }

        await handleShippingRateChange(event, urls, elements);
    });

    expressCheckoutElement.on('confirm', async (event) => {
        // GA4: add_shipping_info fallback for 1-click wallets that skip shippingratechange
        if (!shippingInfoPushed) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'add_shipping_info',
                'ecommerce': {
                    'currency': 'USD',
                    'value': amountInCents / 100,
                    'shipping_tier': selectedShippingMethod?.displayName || 'Wallet Default',
                    'items': buildGa4Items(cartItems)
                }
            });
        }

        // GA4: add_payment_info with wallet type (apple_pay, google_pay, link)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'add_payment_info',
            'ecommerce': {
                'currency': 'USD',
                'value': amountInCents / 100,
                'payment_type': event.expressPaymentType || 'express_checkout',
                'items': buildGa4Items(cartItems)
            }
        });

        await handleExpressCheckoutConfirm(event, urls, stripe, elements, selectedShippingMethod, cartModified, onCleanup);
    });

    expressCheckoutElement.on('cancel', async () => {
        hideProcessingModal();
        shippingInfoPushed = false;
        if (cartModified && onCleanup) {
            await onCleanup();
            cartModified = false;
        }
    });

    // Mount element
    const expressCheckoutDiv = section.querySelector('[data-mcom="express-checkout-element"]');
    if (expressCheckoutDiv) {
        // Add ready event listener to remove shimmer effect and check available methods
        expressCheckoutElement.on('ready', (event) => {
            expressCheckoutDiv.classList.add('stripe-ready');
            console.log('Express Checkout: Element ready', event);

            // Update ARIA states for accessibility
            section.setAttribute('aria-busy', 'false');

            // Remove loading message now that express checkout is ready
            const loadingMessage = section.querySelector('[data-mcom="express-loading-message"]');
            if (loadingMessage) {
                loadingMessage.remove();
            }

            // Check if any payment methods are available
            const availableMethods = event.availablePaymentMethods;
            if (availableMethods) {
                const hasApplePay = availableMethods.applePay === true;
                const hasGooglePay = availableMethods.googlePay === true;
                const hasLink = availableMethods.link === true;

                if (!hasApplePay && !hasGooglePay && !hasLink) {
                    console.log('Express Checkout: No payment methods available, hiding section');
                    section.style.display = 'none';

                    // Hide the parent container and divider on checkout page
                    const parentContainer = section.closest('[data-mcom="express-checkout-container"]');
                    if (parentContainer) {
                        parentContainer.style.display = 'none';
                    }
                }
            }
        });

        expressCheckoutElement.mount(expressCheckoutDiv);
        console.log('Express Checkout: Element mounted successfully');
    } else {
        console.error('Express Checkout: Mount point not found');
    }

    return {
        elements,
        expressCheckoutElement,
        updateAmount: (newAmount) => {
            const amountCents = Math.round(newAmount * 100);
            elements.update({ amount: amountCents });
        }
    };
}

/**
 * Handle shipping address change
 * @param {Object} event - Stripe event
 * @param {Object} urls - URL endpoints
 * @param {Object} elements - Stripe elements instance
 * @param {Function|null} onAddToCart - Optional async callback to add product to cart
 * @param {Function} setCartModified - Callback to update cartModified state
 */
async function handleShippingAddressChange(event, urls, elements, onAddToCart, setCartModified) {
    try {
        if (onAddToCart) {
            const success = await onAddToCart();
            if (!success) {
                event.reject();
                return;
            }
            setCartModified(true);
        }

        const token = getCsrfToken();

        const response = await fetch(urls.shippingOptions, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': token
            },
            body: JSON.stringify({
                name: event.name || '',
                addressLine1: event.address?.line1 || '',
                addressLine2: event.address?.line2 || '',
                city: event.address?.city || '',
                state: event.address?.state || '',
                postalCode: event.address?.postal_code || '',
                country: event.address?.country || '',
                phone: event.phoneNumber || ''
            })
        });

        const data = await response.json();

        if (data.shippingOptions && data.shippingOptions.length > 0) {
            const shippingRates = data.shippingOptions.map(option => ({
                id: option.id,
                displayName: option.label,
                amount: option.amount
            }));

            // Update order summary display
            updateTotals(data);

            // Update Stripe Elements amount
            if (data.orderTotal) {
                const totalAmountCents = Math.round(data.orderTotal * 100);
                elements.update({ amount: totalAmountCents });
            }

            event.resolve({
                shippingRates: shippingRates,
                lineItems: data.lineItems || []
            });
        } else {
            event.reject();
        }
    } catch (error) {
        console.error('Express Checkout shipping address change error:', error);
        event.reject();
    }
}

/**
 * Handle shipping rate change
 * @param {Object} event - Stripe event
 * @param {Object} urls - URL endpoints
 * @param {Object} elements - Stripe elements instance
 */
async function handleShippingRateChange(event, urls, elements) {
    try {
        const token = getCsrfToken();

        const response = await fetch(urls.updateShippingRate, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': token
            },
            body: JSON.stringify({
                shippingRateId: event.shippingRate.id
            })
        });

        if (!response.ok) {
            event.reject();
            return;
        }

        const result = await response.json();

        if (!result.success) {
            event.reject();
            return;
        }

        // Update order display
        updateTotals(result);

        // Update Stripe Elements amount
        if (result.orderTotal) {
            const totalAmountCents = Math.round(result.orderTotal * 100);
            elements.update({ amount: totalAmountCents });
        }

        event.resolve({
            lineItems: result.lineItems || []
        });
    } catch (error) {
        console.error('Error updating shipping rate:', error);
        event.reject();
    }
}

/**
 * Handle Express Checkout confirmation
 * @param {Object} event - Stripe event
 * @param {Object} urls - URL endpoints
 * @param {Object} stripe - Stripe instance
 * @param {Object} elements - Stripe elements instance
 * @param {Object|null} selectedShippingMethod - Selected shipping method
 * @param {boolean} cartModified - Whether cart was modified by onAddToCart
 * @param {Function|null} onCleanup - Optional async callback to clear cart on failure
 */
let isConfirming = false;

// MARSHALLTOWN - Set once the customer has been warned that this looks like an order they
// already placed and has chosen to place it anyway. Sent with the next express attempt so
// the server stops asking. One-shot, so a later duplicate is caught again.
let duplicateOrderConfirmed = false;

async function handleExpressCheckoutConfirm(event, urls, stripe, elements, selectedShippingMethod, cartModified, onCleanup) {
    if (isConfirming) return;
    isConfirming = true;

    const cleanup = async () => {
        if (cartModified && onCleanup) {
            await onCleanup();
        }
    };

    try {
        console.log('Express Checkout Confirm - shippingAddress:', JSON.stringify(event.shippingAddress));
        console.log('Express Checkout Confirm - billingDetails:', JSON.stringify(event.billingDetails));

        // Submit elements
        const submitResult = await elements.submit();

        if (submitResult.error) {
            await cleanup();
            showError(submitResult.error.message);
            return;
        }

        showProcessingModal('processing', EXPRESS_MODAL_OPTIONS);

        // Create confirmation token
        const confirmationTokenResult = await stripe.createConfirmationToken({
            elements: elements
        });

        if (confirmationTokenResult.error) {
            hideProcessingModal();
            await cleanup();
            showError(confirmationTokenResult.error.message);
            return;
        }

        const token = getCsrfToken();

        // Body fields are a fallback; the server retrieves the ConfirmationToken from Stripe.
        const tokenShipping = confirmationTokenResult.confirmationToken.shipping;

        const response = await fetch(urls.confirmOrder, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'RequestVerificationToken': token
            },
            body: JSON.stringify({
                confirmationTokenId: confirmationTokenResult.confirmationToken.id,
                shippingMethodId: selectedShippingMethod?.id,
                // MARSHALLTOWN - True only on the attempt immediately after the customer was
                // warned about a duplicate and chose to place it anyway. One-shot: reset
                // below so a later duplicate is caught again.
                confirmDuplicateOrder: duplicateOrderConfirmed,
                email: event.billingDetails?.email || '',
                shippingName: tokenShipping?.name || event.shippingAddress?.name || event.billingDetails?.name || '',
                shippingPhone: tokenShipping?.phone || event.billingDetails?.phone || '',
                shippingLine1: tokenShipping?.address?.line1 || event.shippingAddress?.address?.line1 || '',
                shippingLine2: tokenShipping?.address?.line2 || event.shippingAddress?.address?.line2 || '',
                shippingCity: tokenShipping?.address?.city || event.shippingAddress?.address?.city || '',
                shippingState: tokenShipping?.address?.state || event.shippingAddress?.address?.state || '',
                shippingPostalCode: tokenShipping?.address?.postal_code || event.shippingAddress?.address?.postal_code || '',
                shippingCountry: tokenShipping?.address?.country || event.shippingAddress?.address?.country || ''
            })
        });

        duplicateOrderConfirmed = false;

        if (!response.ok) {
            hideProcessingModal();
            await cleanup();
            const errorData = await response.json().catch(() => ({
                error: `Server error: ${response.status}`
            }));
            showError(errorData.error || 'Order confirmation failed');
            return;
        }

        const data = await response.json();

        if (data.requiresDuplicateConfirmation) {
            // MARSHALLTOWN - This looks like an order the customer already placed, usually
            // because they never saw their confirmation and tried again. Ask before taking a
            // second order and a second payment (Zammad 201979). Nothing has been placed or
            // charged: the server returned before placing the order.
            //
            // Unlike the single-page checkout we cannot resubmit for them. The express
            // payment sheet and its confirmation token are consumed by this attempt, so the
            // customer has to start a fresh one. Arm the flag and tell them to tap again.
            hideProcessingModal();
            await cleanup();

            const placeAnyway = window.confirm(
                `${data.error || 'It looks like you already placed this order a few minutes ago.'}\n\n` +
                'Click OK if you meant to order twice, then use Express Pay once more to place it. ' +
                'Click Cancel to stop.');

            if (placeAnyway) {
                duplicateOrderConfirmed = true;
                showError('Use Express Pay again to place this order a second time.');
            } else {
                showError('Your order was not placed. Check your account for the order you already placed, ' +
                    'or contact Customer Care if you need help.');
            }

            return;
        }

        if (!data.success) {
            hideProcessingModal();
            await cleanup();
            showError(data.error || 'Order confirmation failed');
            return;
        }

        // Retrieve payment intent
        const paymentIntent = await stripe.retrievePaymentIntent(data.clientSecret);

        if (paymentIntent.error) {
            hideProcessingModal();
            await cleanup();
            showError(paymentIntent.error.message);
            return;
        }

        // Handle 3D Secure if needed
        if (paymentIntent.paymentIntent.status === 'requires_action') {
            const confirmResult = await stripe.confirmPayment({
                clientSecret: data.clientSecret,
                confirmParams: {
                    return_url: data.redirectUrl || window.location.origin + '/checkout/completed'
                },
                redirect: 'if_required'
            });

            if (confirmResult.error) {
                hideProcessingModal();
                await cleanup();
                showError(confirmResult.error.message);
            } else {
                showProcessingModal('success', EXPRESS_MODAL_OPTIONS);
                window.location.href = data.redirectUrl;
            }
        } else if (paymentIntent.paymentIntent.status === 'succeeded' ||
                   paymentIntent.paymentIntent.status === 'requires_capture') {
            showProcessingModal('success', EXPRESS_MODAL_OPTIONS);
            window.location.href = data.redirectUrl;
        } else {
            hideProcessingModal();
            await cleanup();
            showError('Payment processing incomplete. Status: ' + paymentIntent.paymentIntent.status);
        }
    } catch (error) {
        hideProcessingModal();
        await cleanup();
        console.error('Express Checkout confirm error:', error);
        showError('Payment confirmation failed');
    } finally {
        isConfirming = false;
    }
}
